import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReportView from "../components/ReportView";
import LiturgicalLibrarySection from "../components/LiturgicalLibrarySection";
import WorshipMusicSection from "../components/WorshipMusicSection";
import MusicResourcesSection from "../components/MusicResourcesSection";
import {
  WORSHIP_MUSIC_HIGH_VALUE,
  STATES_PROVINCES_TERRITORIES,
  EUCHARISTIC_PRAYER_BY_DENOMINATION,
  SERVICE_MUSIC_SUGGESTIONS,
  resolveTraditionKey,
} from "./parishIntelligenceOptions";
import { track } from "../lib/track";
import "./ParishProfile.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PLANNING_SCOPES = ["Single Sunday", "Liturgical Season", "Holy Week", "Christmas Cycle", "Annual Plan"];

const LITURGICAL_TRADITIONS = ["Roman Catholic", "Episcopal / Anglican", "Lutheran", "Methodist", "Presbyterian", "Baptist", "AME", "AME Zion", "CME", "Church of God in Christ", "Non-denominational", "Other"];
const WORSHIP_HERITAGE = ["African American", "Black Episcopal", "Black Catholic", "AME", "AME Zion", "CME", "National Baptist", "Progressive Baptist", "COGIC", "Gospel-centered", "Spirituals-centered", "Multicultural Black Church", "Anglo-Catholic", "Broad Church", "Evangelical", "Gospel", "Traditional", "Contemporary", "Blended", "Hispanic / Latino", "Caribbean", "African", "Multicultural", "Other"];
const MUSICAL_HERITAGE = ["African American Spirituals", "Traditional Gospel", "Contemporary Gospel", "Lift Every Voice and Sing II", "African American Heritage Hymnal", "New National Baptist Hymnal", "Church of God in Christ repertoire", "Episcopal Hymnal 1982", "Wonder, Love, and Praise", "Roman Catholic hymnals", "Lutheran Chorales", "Methodist Hymns", "Taizé", "Iona", "Contemporary Worship", "Praise & Worship"];
const WORSHIP_CHARACTERISTICS = ["High ceremonial", "Broad Church", "Low Church", "Formal", "Informal", "Congregation-led singing", "Choir-centered", "Organ-centered", "Band-led", "Chanted liturgy", "Spoken liturgy", "Incense", "Processions", "Responsive prayer", "Extended music ministry", "Other"];
const CONGREGATIONAL_IDENTITY = ["Urban", "Rural", "Suburban", "University", "Cathedral", "Historic parish", "Mission church", "Multicultural", "Intergenerational", "Other"];
const WORSHIP_RHYTHM = ["Rite I", "Rite II", "Traditional Language", "Contemporary Language", "Sung Eucharist", "Choral Eucharist", "Weekly Communion", "Monthly Communion", "Morning Prayer", "Evensong", "Praise Service", "Healing Service", "Family Service", "Revival / Extended Praise", "Testimony / Witness", "Altar Call", "Seasonal Devotions", "Marian Devotions", "Stations of the Cross", "Other"];
const MUSICAL_FORCES = ["Organ", "Piano", "Choir", "Gospel Choir", "Schola", "Cantor", "Psalmist", "Praise Team", "Band", "Guitar", "Bass", "Drums", "Brass", "Strings", "Woodwinds", "Handbells", "Children's Choir", "Youth Choir", "Soloist", "Small Ensemble", "Orchestra", "No Regular Musicians", "Other"];
const MUSIC_AUTHORITY = ["Director of Music", "Pastor / Priest", "Worship Planning Team", "Music Committee", "Choir Director", "Cantor", "Collaborative Process", "Other"];

// === Structured selection lists — Phase 2 profile normalization ===
// Reduce free-text where finite, recognisable sets exist. 90/10 rule.
const SACRED_PRACTICE = ["Traditional", "Contemporary", "Blended", "Liturgical", "Anglo-Catholic", "Broad Church", "Evangelical", "Charismatic", "Monastic", "Family-Oriented", "Mission-Oriented", "Multicultural", "Other"];
const MUSIC_STYLE = ["Gregorian Chant", "Renaissance Polyphony", "Anglican Choral Tradition", "Classical Sacred Music", "Traditional Hymnody", "Contemporary Worship", "Gospel", "Spirituals", "Taizé", "Praise & Worship", "Folk", "Jazz Influences", "Orchestra", "Brass Ensemble", "Organ-Led", "Piano-Led", "Mixed Instrumentation", "Other"];
const TIME_ZONES_US = ["Eastern (ET)", "Central (CT)", "Mountain (MT)", "Pacific (PT)", "Alaska (AKT)", "Hawaii–Aleutian (HAT)", "Atlantic (AT)", "Other"];
const LANGUAGES = ["English", "Spanish", "French", "Latin", "Greek", "Haitian Creole", "Swahili", "Portuguese", "Korean", "Chinese (Mandarin)", "Chinese (Cantonese)", "Vietnamese", "Tagalog", "American Sign Language", "Other"];
const ATTENDANCE_BANDS = ["Under 25", "25–50", "51–100", "101–200", "201–500", "501–1000", "1000+"];
const SERVICE_TYPES = ["Eucharist / Mass", "Morning Prayer", "Evening Prayer / Evensong", "Compline", "Sung Eucharist", "Choral Eucharist", "Praise Service", "Family Service", "Healing Service", "Revival / Extended Praise", "Vespers", "Baptism", "Ordination", "Wedding", "Funeral", "Ecumenical / Special", "Other"];
const CLERGY_ROLES = ["Rector", "Vicar", "Priest", "Priest-in-Charge", "Priest Associate", "Pastor", "Bishop", "Dean", "Deacon", "Curate", "Interim Rector", "Assistant Rector", "Chaplain", "Other"];
const PREFERRED_LITURGICAL_STYLE = ["Rite I", "Rite II", "Traditional Language", "Contemporary Language", "Sung Eucharist", "Choral Eucharist", "Blended", "Other"];
const SERMON_LENGTH = ["Under 10 minutes", "10–15 minutes", "15–20 minutes", "20–30 minutes", "30+ minutes"];
const COMFORT_LEVEL = ["None", "Basic", "Moderate", "Extensive", "Expert"];
const CEREMONIAL_LEVEL = ["Low Church", "Broad Church", "High Church", "Anglo-Catholic", "Ceremonial Mixed"];
const BULLETIN_FORMATS = ["Full-text bulletin", "Order-only bulletin", "Hymn numbers only", "Digital / on-screen only", "Multi-page booklet", "Weekly leaflet", "Other"];
const LICENSING = ["OneLicense", "CCLI", "LicenSing", "Public Domain only", "Copyright cleared per work", "Multiple licenses", "Other"];
const LECTIONARIES = ["Revised Common Lectionary (RCL)", "Roman Catholic Lectionary", "BCP 1979 Daily Office", "Lutheran Service Book Three-Year", "Common Worship (CofE)", "Other"];
const PSALTERS = ["BCP 1979 Psalter", "Lift Every Voice and Sing II", "Grail Psalter", "Anglican Chant Psalter", "Common Worship Psalter", "Simplified Anglican Chant", "Other"];
const HYMNALS = ["The Hymnal 1982", "Wonder, Love, and Praise", "Lift Every Voice and Sing II", "African American Heritage Hymnal", "New National Baptist Hymnal", "Roman Catholic Worship IV", "Gather", "Evangelical Lutheran Worship", "United Methodist Hymnal", "Presbyterian Hymnal (Glory to God)", "Voices Together", "Sing to the Lord", "Other"];
const SOUND_SYSTEMS = ["Full PA + multiple mics", "Basic PA", "Wireless mics only", "Ambient / no reinforcement", "Other"];
const LIVESTREAM_SETUP = ["Full multi-camera", "Single camera", "Phone / basic camera", "Audio livestream only", "None", "Other"];
const LOCATIONS = ["Front left", "Front right", "Center front", "Rear balcony", "Rear ground floor", "Chancel", "Choir loft", "Not applicable", "Other"];
const STAFF_DEPARTMENTS = ["Music", "Liturgy", "Administration", "Formation / Christian Ed", "Outreach", "Facilities", "Communications", "Development", "Youth Ministry", "Children's Ministry", "Pastoral Care", "Other"];
const DEFAULT_TONES = ["Traditional", "Contemporary", "Formal", "Warm & pastoral", "Scholarly", "Reverent", "Celebratory", "Contemplative"];
const REPORT_LENGTHS = ["Brief", "Standard", "Detailed", "Comprehensive"];

const TABS = [
  { key: "identity", label: "Parish", type: "object", groups: [
    { title: "Parish Information", subtitle: "Basic information used throughout Liturgical Intelligence™.", fields: [
      ["parish_name", "Parish Name", "text", true],
      ["denomination", "Denomination", "select", false, LITURGICAL_TRADITIONS],
      ["diocese", "Diocese / Judicatory"],
      ["website", "Website"],
    ]},
    { title: "Location", subtitle: "Where the assembly gathers.", fields: [
      ["address", "Address"],
      ["city", "City"],
      ["state", "State, province, or territory", "select", false, STATES_PROVINCES_TERRITORIES],
      ["time_zone", "Time Zone", "select", false, TIME_ZONES_US],
    ]},
    { title: "Primary Contact", subtitle: "Who Ordo AI™ should reach for follow-ups and dossiers.", fields: [
      ["contact_name", "Contact Name"],
      ["contact_email", "Contact Email"],
      ["contact_phone", "Contact Phone"],
    ]},
    { title: "The Assembly", subtitle: "Help us understand the people you serve.", fields: [
      ["languages", "Primary Language(s)", "multi", false, LANGUAGES],
      ["avg_attendance", "Average Sunday Attendance", "select", false, ATTENDANCE_BANDS],
      ["worship_tradition", "Sacred Practice", "multi", false, SACRED_PRACTICE],
    ]},
  ]},
  { key: "parish_dna", label: "Parish DNA", type: "object", groups: [
    { title: "Mission & Vision", subtitle: "The purpose and future your parish is called toward.", fields: [
      ["mission_statement", "Mission Statement", "area"],
      ["vision_statement", "Vision Statement", "area"],
      ["core_values", "Core Values", "area"],
    ]},
    { title: "Parish Character", subtitle: "The identity that makes your parish itself and no other.", fields: [
      ["patron_saint", "Patron Saint / Parish Dedication"],
      ["parish_motto", "Parish Motto"],
      ["community_priorities", "Community Priorities", "area"],
    ]},
    { title: "Worship & Musical Philosophy", subtitle: "How your parish understands worship and sacred music.", fields: [
      ["worship_philosophy", "Worship Philosophy", "area"],
      ["musical_philosophy", "Musical Philosophy", "area"],
    ]},
    { title: "Strategic Context", subtitle: "The strengths, challenges, and goals guiding decisions.", fields: [
      ["parish_strengths", "Parish Strengths", "area"],
      ["current_challenges", "Current Challenges", "area"],
      ["strategic_goals", "Strategic Goals", "area"],
    ]},
  ]},
  { key: "worship_identity", label: "Congregational Identity™", type: "object", groups: [
    { title: "Liturgical Tradition", subtitle: "The rite and heritage that shape your worship.", fields: [
      ["liturgical_tradition", "Liturgical Tradition", "select", false, LITURGICAL_TRADITIONS],
      ["worship_heritage", "Worship & Cultural Heritage", "multi", false, WORSHIP_HERITAGE],
    ]},
    { title: "Musical Vocabulary™", subtitle: "The repertoire and forces that carry your parish's voice.", fields: [
      ["musical_heritage", "Musical Heritage", "multi", false, MUSICAL_HERITAGE],
      ["musical_forces", "Musical Forces™", "multi", false, MUSICAL_FORCES],
    ]},
    { title: "Worship Practice", subtitle: "How your assembly worships — the practices, rhythm, and posture.", fields: [
      ["worship_characteristics", "Worship Practices", "multi", false, WORSHIP_CHARACTERISTICS],
      ["worship_rhythm", "Worship Rhythm™", "multi", false, WORSHIP_RHYTHM],
    ]},
    { title: "Community & Authority", subtitle: "The context of your parish and who shapes its music.", fields: [
      ["congregational_identity", "Community Context", "multi", false, CONGREGATIONAL_IDENTITY],
      ["music_selection_authority", "Music Selection Authority", "select", false, MUSIC_AUTHORITY],
    ]},
  ]},
  { key: "worship_services", label: "Worship", type: "list", itemName: "service_name", addLabel: "Add Service", fields: [
    ["service_name", "Service Name"],
    ["day", "Day", "select", false, DAYS],
    ["time", "Time"],
    ["language", "Language", "select", false, LANGUAGES],
    ["service_type", "Service Type", "select", false, SERVICE_TYPES],
    ["typical_attendance", "Typical Attendance", "select", false, ATTENDANCE_BANDS],
    ["music_style", "Music Style", "multi", false, MUSIC_STYLE],
    ["livestream", "Livestream?", "yesno"],
    ["seasonal_notes", "Seasonal Changes / Notes", "area"],
  ]},
  { key: "clergy", label: "Clergy", type: "list", itemName: "name", addLabel: "Add Clergy", fields: [
    ["name", "Name"],
    ["role", "Role / Title", "select", false, CLERGY_ROLES],
    ["email", "Email"],
    ["preferred_liturgical_style", "Preferred Liturgical Style", "select", false, PREFERRED_LITURGICAL_STYLE],
    ["preferred_eucharistic_prayer", "Preferred Eucharistic Prayer(s)", "multi_denom", false, EUCHARISTIC_PRAYER_BY_DENOMINATION],
    ["sermon_length", "Sermon Length Preference", "select", false, SERMON_LENGTH],
    ["chant_comfort", "Chant Comfort Level", "select", false, COMFORT_LEVEL],
    ["ceremonial_level", "Ceremonial Level", "select", false, CEREMONIAL_LEVEL],
    ["notes", "Notes", "area"],
  ]},
  { key: "worship_music", label: "Worship & Music", type: "worship_music" },
  { key: "music_resources", label: "Music Resources", type: "music_resources" },
  { key: "resources", label: "Resources", type: "object", groups: [
    { title: "Hymnals & Music Sources", subtitle: "The books and repertoire your assembly sings from.", fields: [
      ["hymnals", "Hymnal(s) Used", "multi", false, HYMNALS],
      ["service_music", "Service Music Settings", "tags", false, SERVICE_MUSIC_SUGGESTIONS],
      ["approved_sources", "Approved Music Sources", "area"],
    ]},
    { title: "Liturgical Resources", subtitle: "The lectionary, psalter, and bulletin you rely on.", fields: [
      ["psalter", "Psalter Used", "select", false, PSALTERS],
      ["lectionary", "Lectionary Used", "select", false, LECTIONARIES],
      ["bulletin_format", "Bulletin Format", "select", false, BULLETIN_FORMATS],
    ]},
    { title: "Licensing", subtitle: "How your parish clears music for public worship.", fields: [
      ["copyright_info", "Copyright / License Coverage", "multi", false, LICENSING],
    ]},
    { title: "Notes", subtitle: "Anything else worth remembering.", fields: [
      ["notes", "Notes", "area"],
    ]},
  ]},
  { key: "customs", label: "Customs", type: "customs",
    seasons: ["Advent", "Christmas", "Epiphany", "Lent", "Holy Week", "Easter", "Pentecost", "Patronal Feast", "Local Traditions"],
    fields: [
      ["customs_observed", "Customs Observed", "area"], ["music_traditions", "Music Traditions", "area"],
      ["liturgical_practices", "Liturgical Practices", "area"], ["clergy_preferences", "Clergy Preferences", "area"], ["notes", "Notes", "area"],
    ]},
  { key: "music_preferences", label: "Music Prefs", type: "object", groups: [
    { title: "Frequent Repertoire", subtitle: "The music your assembly returns to season after season.", fields: [
      ["frequent_hymns", "Frequently Used Hymns", "tags"],
      ["favorite_tunes", "Favorite Hymn Tunes", "tags"],
      ["preferred_composers", "Preferred Composers", "tags"],
      ["service_music_prefs", "Service Music Preferences", "area"],
    ]},
    { title: "Musical Boundaries", subtitle: "What fits your parish — and what to avoid.", fields: [
      ["contemporary_allowed", "Contemporary Music Allowed?", "yesno"],
      ["gospel_allowed", "Gospel Music Allowed?", "yesno"],
      ["taize_allowed", "Taizé Allowed?", "yesno"],
      ["praise_band_allowed", "Praise Band Allowed?", "yesno"],
      ["music_to_avoid", "Music to Avoid", "area"],
    ]},
    { title: "Notes", subtitle: "Anything else Ordo AI™ should keep in mind.", fields: [
      ["notes", "Notes", "area"],
    ]},
  ]},
  { key: "facilities", label: "Facilities", type: "object", groups: [
    { title: "Seating & Locations", subtitle: "The room and where the musicians sit within it.", fields: [
      ["seating_capacity", "Seating Capacity"],
      ["choir_location", "Choir Location", "select", false, LOCATIONS],
      ["organ_location", "Organ Location", "select", false, LOCATIONS],
      ["piano_location", "Piano Location", "select", false, LOCATIONS],
    ]},
    { title: "Sound & Technology", subtitle: "The systems that carry the assembly's voice beyond the room.", fields: [
      ["sound_system", "Sound System", "select", false, SOUND_SYSTEMS],
      ["projection_screens", "Projection Screens?", "yesno"],
      ["livestream_setup", "Livestream Setup", "select", false, LIVESTREAM_SETUP],
    ]},
    { title: "Accessibility & Sacristy", subtitle: "Where the assembly is welcomed and clergy prepare.", fields: [
      ["accessibility_notes", "Accessibility Notes", "area"],
      ["sacristy_notes", "Sacristy / Vesting Notes", "area"],
      ["notes", "Notes", "area"],
    ]},
  ]},
  { key: "staff", label: "Staff", type: "list", itemName: "name", addLabel: "Add Staff Member", fields: [
    ["name", "Name"], ["role", "Role"], ["email", "Email"], ["phone", "Phone"],
    ["department", "Department / Ministry", "select", false, STAFF_DEPARTMENTS],
    ["notes", "Notes", "area"],
  ]},
  { key: "planning_preferences", label: "Planning", type: "object", groups: [
    { title: "Default Scope & Tone", subtitle: "How Ordo AI™ should approach a dossier by default.", fields: [
      ["default_scope", "Default Planning Scope", "select", false, PLANNING_SCOPES],
      ["default_tone", "Default Tone", "select", false, DEFAULT_TONES],
      ["report_length", "Preferred Report Length", "select", false, REPORT_LENGTHS],
    ]},
    { title: "Sections to Include", subtitle: "The parts of every dossier that matter to your parish.", fields: [
      ["bulletin_copy", "Bulletin Copy Needed?", "yesno"],
      ["choir_notes", "Choir Notes Needed?", "yesno"],
      ["clergy_notes", "Clergy Notes Needed?", "yesno"],
      ["voluntaries", "Organ / Piano Voluntaries Needed?", "yesno"],
      ["worship_team_notes", "Worship Team Notes Needed?", "yesno"],
      ["final_checklist", "Final Checklist Needed?", "yesno"],
    ]},
    { title: "Additional Guidance", subtitle: "Anything else Ordo AI™ should always — or never — do.", fields: [
      ["always_include", "Always Include Sections", "area"],
      ["never_include", "Never Include Sections", "area"],
      ["notes", "Notes", "area"],
    ]},
  ]},
  { key: "insights", label: "AI Insights", type: "insights" },
];

const seasonKey = (s) => s.toLowerCase().replace(/[^a-z]+/g, "_");

const TAB_WEIGHTS = {
  identity: 3, parish_dna: 3, worship_identity: 3, worship_services: 3, worship_music: 3, music_resources: 3, customs: 3, planning_preferences: 3,
  clergy: 2, resources: 2, music_preferences: 2, facilities: 1, staff: 1,
};

function isFilled(v) { return v != null && v.toString().trim() !== ""; }

function tabFraction(t, profile) {
  if (t.type === "worship_music") {
    const data = profile.worship_music || {};
    const filled = WORSHIP_MUSIC_HIGH_VALUE.filter((k) => isFilled(data[k])).length;
    return filled / WORSHIP_MUSIC_HIGH_VALUE.length;
  }
  if (t.type === "music_resources") {
    const data = profile.music_resources || {};
    const HIGH = ["tradition", "hymnals_owned", "preferred_styles",
                  "choir_voicing", "congregational_difficulty"];
    const filled = HIGH.filter((k) => {
      const v = data[k];
      if (Array.isArray(v)) return v.length > 0;
      return isFilled(v);
    }).length;
    return filled / HIGH.length;
  }
  if (t.type === "object") {
    const data = profile[t.key] || {};
    const allFields = t.groups
      ? t.groups.flatMap((g) => g.fields)
      : (t.fields || []);
    const filled = allFields.filter((f) => isFilled(data[f[0]])).length;
    return allFields.length ? filled / allFields.length : 0;
  }
  if (t.type === "list") {
    const list = profile[t.key] || [];
    return list.some((it) => Object.values(it || {}).some(isFilled)) ? 1 : 0;
  }
  if (t.type === "customs") {
    const c = profile.customs || {};
    const done = t.seasons.filter((s) => Object.values(c[seasonKey(s)] || {}).some(isFilled)).length;
    return t.seasons.length ? done / t.seasons.length : 0;
  }
  return null;
}


function Field({ def, value, onChange, siblings, onSiblingChange, traditionKey }) {
  const [k, label, t = "text", , options] = def;
  const required = def[3];
  const otherKey = `other_${k}`;
  const otherValue = siblings ? siblings[otherKey] || "" : "";

  // For select / multi: sibling specifier appears when "Other" is chosen.
  const showOther = (kind) => {
    if (kind === "select") return value === "Other";
    if (kind === "multi" || kind === "multi_denom") {
      return Array.isArray(value) && value.includes("Other");
    }
    return false;
  };
  const OtherSpecifier = ({ kind }) => (
    <input
      className="pp-other-input"
      value={otherValue}
      onChange={(e) => onSiblingChange && onSiblingChange(otherKey, e.target.value)}
      placeholder={`Please specify${label ? " " + label.toLowerCase() : ""}…`}
      data-testid={`pf-${k}-other-input`}
    />
  );

  return (
    <div className={`pp-field${t === "area" || t === "multi" || t === "multi_denom" || t === "tags" ? " full" : ""}`}>
      <label>{label}{required ? " *" : ""}</label>
      {t === "area" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-${k}`} />
      ) : t === "tags" ? (
        <TagsInput
          value={value}
          onChange={onChange}
          suggestions={options || []}
          fieldKey={k}
        />
      ) : t === "multi_denom" ? (
        <>
          <MultiDenomChips
            value={value}
            onChange={onChange}
            optionsByKey={options || {}}
            traditionKey={traditionKey}
            fieldKey={k}
          />
          {showOther("multi_denom") && <OtherSpecifier kind="multi_denom" />}
        </>
      ) : t === "multi" ? (
        <>
          <div className="pp-multi" data-testid={`pf-${k}`}>
            {(options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const on = arr.includes(o);
              const slug = o.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <button type="button" key={o} className={`pp-chip${on ? " on" : ""}`}
                  data-testid={`pf-${k}-${slug}`}
                  onClick={() => onChange((prev) => {
                    const a = Array.isArray(prev) ? prev : [];
                    return a.includes(o) ? a.filter((x) => x !== o) : [...a, o];
                  })}>
                  {o}
                </button>
              );
            })}
          </div>
          {showOther("multi") && <OtherSpecifier kind="multi" />}
        </>
      ) : t === "yesno" ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-${k}`}>
          <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
        </select>
      ) : t === "select" ? (
        <>
          <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-${k}`}>
            <option value="">—</option>{(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {showOther("select") && <OtherSpecifier kind="select" />}
        </>
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-${k}`} />
      )}
    </div>
  );
}

/* ---------- Tag input ---------------------------------------------------
 * Chip list of free-text values. Users can:
 *   - Click a suggestion chip below to add it
 *   - Type in the input and press Enter (or comma) to add a custom value
 *   - Click × on any chip to remove
 * Values are NEVER silently altered. Legacy string values are wrapped
 * verbatim into a single tag on first render (see normalizeLegacyProfile).
 * =====================================================================*/
function TagsInput({ value, onChange, suggestions, fieldKey }) {
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(value) ? value : (value ? [String(value)] : []);
  const add = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return;
    if (arr.includes(s)) { setDraft(""); return; }
    onChange([...arr, s]);
    setDraft("");
  };
  const remove = (idx) => {
    const next = arr.slice(); next.splice(idx, 1); onChange(next);
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add(draft); }
    else if (e.key === "Backspace" && !draft && arr.length) {
      // Backspace on empty input removes the last tag.
      remove(arr.length - 1);
    }
  };
  // Suggestions that aren't already selected.
  const remaining = (suggestions || []).filter((s) => !arr.includes(s));

  return (
    <div className="pp-tags" data-testid={`pf-${fieldKey}`}>
      <div className="pp-tags-row">
        {arr.map((t, i) => (
          <span key={`${t}-${i}`} className="pp-tag" data-testid={`pf-${fieldKey}-tag-${i}`}>
            {t}
            <button
              type="button"
              className="pp-tag-x"
              onClick={() => remove(i)}
              aria-label={`Remove ${t}`}
              data-testid={`pf-${fieldKey}-remove-${i}`}
            >×</button>
          </span>
        ))}
        <input
          className="pp-tags-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => draft && add(draft)}
          placeholder={arr.length ? "Add another…" : "Type and press Enter"}
          data-testid={`pf-${fieldKey}-input`}
        />
      </div>
      {remaining.length > 0 && (
        <div className="pp-tags-suggest" data-testid={`pf-${fieldKey}-suggest`}>
          <span className="pp-tags-suggest-label">Suggestions</span>
          {remaining.slice(0, 12).map((s) => (
            <button
              type="button"
              key={s}
              className="pp-tags-suggest-chip"
              onClick={() => add(s)}
              data-testid={`pf-${fieldKey}-suggest-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Denomination-aware multi (Eucharistic Prayer) --------------
 * Options are keyed by tradition. Field looks up the parish's tradition
 * from the top-level profile (identity.denomination →
 * worship_identity.liturgical_tradition → worship_music.primary_worship_tradition).
 * Falls back to the "General" list when no known tradition is set.
 * =====================================================================*/
function MultiDenomChips({ value, onChange, optionsByKey, traditionKey, fieldKey }) {
  const activeKey = optionsByKey[traditionKey] ? traditionKey : "General";
  const options = optionsByKey[activeKey] || [];
  const arr = Array.isArray(value) ? value : (value ? [String(value)] : []);

  // If value contains tokens NOT in the current option list (e.g. because
  // the tradition was switched, or a legacy import), still render them as
  // active chips so nothing is silently dropped from view.
  const extras = arr.filter((v) => !options.includes(v) && v !== "Other");
  const displayOptions = [...options];
  extras.forEach((e) => { if (!displayOptions.includes(e)) displayOptions.splice(displayOptions.length - 1, 0, e); });

  const toggle = (o) => {
    const on = arr.includes(o);
    onChange(on ? arr.filter((x) => x !== o) : [...arr, o]);
  };

  return (
    <>
      <div className="pp-multi" data-testid={`pf-${fieldKey}`}>
        {displayOptions.map((o) => {
          const on = arr.includes(o);
          const slug = String(o).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <button
              type="button"
              key={o}
              className={`pp-chip${on ? " on" : ""}`}
              onClick={() => toggle(o)}
              data-testid={`pf-${fieldKey}-${slug}`}
            >{o}</button>
          );
        })}
      </div>
      {activeKey === "General" && (
        <p className="pp-field-hint" data-testid={`pf-${fieldKey}-hint`}>
          Options tailored to your parish's tradition will appear once denomination is set.
        </p>
      )}
    </>
  );
}

/* ---------- Read-side legacy normalizer --------------------------------
 * On profile load, transform legacy free-text values into the new
 * structured shape WITHOUT touching the server. Original values are
 * preserved verbatim under the `other_{field}` sibling.
 *
 * • state (identity)                              → select + other_state
 * • preferred_eucharistic_prayer (clergy items)   → array + other_preferred_eucharistic_prayer
 * • service_music (resources)                     → array + other_service_music
 * • frequent_hymns / favorite_tunes /
 *   preferred_composers (music_preferences)       → array (single-tag wrap)
 * =====================================================================*/
function normalizeLegacyProfile(profile) {
  if (!profile || typeof profile !== "object") return profile;
  const p = { ...profile };

  // identity.state
  const id = { ...(p.identity || {}) };
  if (typeof id.state === "string" && id.state.trim()) {
    if (!STATES_PROVINCES_TERRITORIES.includes(id.state)) {
      id.other_state = id.other_state || id.state;
      id.state = "Other";
    }
    p.identity = id;
  }

  // clergy list — each item's preferred_eucharistic_prayer
  if (Array.isArray(p.clergy)) {
    const clergyTraditionKey = resolveTraditionKey(p);
    const allEnumValues = new Set(
      Object.values(EUCHARISTIC_PRAYER_BY_DENOMINATION).flat()
    );
    p.clergy = p.clergy.map((c) => {
      if (!c || typeof c !== "object") return c;
      const item = { ...c };
      const v = item.preferred_eucharistic_prayer;
      if (typeof v === "string" && v.trim()) {
        if (allEnumValues.has(v)) {
          item.preferred_eucharistic_prayer = [v];
        } else {
          item.other_preferred_eucharistic_prayer =
            item.other_preferred_eucharistic_prayer || v;
          item.preferred_eucharistic_prayer = ["Other"];
        }
      }
      return item;
    });
    // touch clergyTraditionKey to satisfy lint even if unused when array empty
    void clergyTraditionKey;
  }

  // resources.service_music
  const res = { ...(p.resources || {}) };
  if (typeof res.service_music === "string" && res.service_music.trim()) {
    res.service_music = [res.service_music]; // preserve verbatim as a single tag
    p.resources = res;
  }

  // music_preferences: three tag-input fields
  const mp = { ...(p.music_preferences || {}) };
  for (const key of ["frequent_hymns", "favorite_tunes", "preferred_composers"]) {
    if (typeof mp[key] === "string" && mp[key].trim()) {
      mp[key] = [mp[key]]; // preserve verbatim
    }
  }
  p.music_preferences = mp;

  return p;
}

export default function ParishProfile() {
  const navigate = useNavigate();
  const [active, setActive] = useState("identity");
  const [profile, setProfile] = useState({ customs: {} });
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [openSeason, setOpenSeason] = useState("");

  // AI insights state
  const [genLoading, setGenLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [memStats, setMemStats] = useState(null);
  const [catalog, setCatalog] = useState({ resources: [], publishers: [], resource_types: [] });

  useEffect(() => {
    track("Parish Profile Started");
    axios.get(`${API}/parish-profile`).then((r) => {
      const raw = r.data.profile || {};
      const normalized = normalizeLegacyProfile(raw);
      setProfile({ customs: {}, ...normalized });
      setSavedAt(r.data.updated_at);
    }).catch(() => {}).finally(() => setLoaded(true));
    axios.get(`${API}/parish/memory/stats`).then((r) => setMemStats(r.data)).catch(() => {});
    axios.get(`${API}/liturgical-library/catalog`).then((r) => setCatalog(r.data || {})).catch(() => {});
  }, []);

  const save = useCallback(async (silent) => {
    if (!silent) setSaving(true);
    try {
      let r;
      if (active === "worship_identity") {
        // Migrated to safe section PATCH — deep-merges, never wipes unrelated sections.
        r = await axios.patch(`${API}/parish-profile`, { updates: { worship_identity: profile.worship_identity || {} } });
      } else {
        // Legacy full-profile PUT. Guard: never replace the stored profile before it has loaded.
        if (!loaded) {
          if (!silent) alert("Profile is still loading. Please wait a moment before saving.");
          return;
        }
        r = await axios.put(`${API}/parish-profile`, { profile });
      }
      setSavedAt(r.data.updated_at);
      if (r.data.memories_flagged_for_review > 0) {
        axios.get(`${API}/parish/memory/stats`).then((s) => setMemStats(s.data)).catch(() => {});
      }
    } catch (e) { if (!silent) alert("Could not save the profile."); }
    finally { if (!silent) setSaving(false); }
  }, [profile, active, loaded]);

  const profileRef = useRef(profile);
  profileRef.current = profile;
  const switchTab = (key) => { if (loaded) save(true); setActive(key); };

  const setObj = (section, k, v) => setProfile((p) => {
    const prev = p[section] || {};
    const next = typeof v === "function" ? v(prev[k]) : v;
    return { ...p, [section]: { ...prev, [k]: next } };
  });
  const setItem = (section, idx, k, v) => setProfile((p) => {
    const list = [...(p[section] || [])];
    const prevItem = list[idx] || {};
    const next = typeof v === "function" ? v(prevItem[k]) : v;
    list[idx] = { ...prevItem, [k]: next }; return { ...p, [section]: list };
  });
  const addItem = (section) => setProfile((p) => ({ ...p, [section]: [...(p[section] || []), {}] }));
  const removeItem = (section, idx) => setProfile((p) => {
    const list = [...(p[section] || [])]; list.splice(idx, 1); return { ...p, [section]: list };
  });
  const setCustom = (season, k, v) => setProfile((p) => {
    const sk = seasonKey(season);
    const prevSeason = (p.customs || {})[sk] || {};
    const next = typeof v === "function" ? v(prevSeason[k]) : v;
    return { ...p, customs: { ...(p.customs || {}), [sk]: { ...prevSeason, [k]: next } } };
  });

  const tab = TABS.find((t) => t.key === active);

  // Denomination-aware context for fields like preferred_eucharistic_prayer.
  const traditionKey = useMemo(() => resolveTraditionKey(profile), [profile]);

  // Sibling-write helpers for the "Other—specify" pattern. Writes to the
  // same section object at key `other_{field_key}` without disturbing
  // sibling fields.
  const setObjSibling = (section, siblingKey, v) => setProfile((p) => {
    const prev = p[section] || {};
    return { ...p, [section]: { ...prev, [siblingKey]: v } };
  });
  const setItemSibling = (section, idx, siblingKey, v) => setProfile((p) => {
    const list = [...(p[section] || [])];
    const prevItem = list[idx] || {};
    list[idx] = { ...prevItem, [siblingKey]: v };
    return { ...p, [section]: list };
  });
  const setCustomSibling = (season, siblingKey, v) => setProfile((p) => {
    const sk = seasonKey(season);
    const prevSeason = (p.customs || {})[sk] || {};
    return { ...p, customs: { ...(p.customs || {}), [sk]: { ...prevSeason, [siblingKey]: v } } };
  });

  const completeness = useMemo(() => {
    let num = 0, den = 0; const frac = {};
    for (const t of TABS) {
      const w = TAB_WEIGHTS[t.key];
      if (!w) continue;
      const f = tabFraction(t, profile);
      frac[t.key] = f; num += w * f; den += w;
    }
    const percent = den ? Math.round((num / den) * 100) : 0;
    const sugg = TABS
      .filter((t) => TAB_WEIGHTS[t.key] && frac[t.key] < 0.8)
      .sort((a, b) => (TAB_WEIGHTS[b.key] - TAB_WEIGHTS[a.key]) || (frac[a.key] - frac[b.key]))
      .slice(0, 2).map((t) => t.label);
    return { percent, frac, sugg };
  }, [profile]);

  const generate = async () => {
    setGenLoading(true); setReport(null); setUpgrade(null);
    await save(true);
    try {
      const start = await axios.post(`${API}/parish-profile/insights`);
      const jobId = start.data.job_id;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/parish/job/${jobId}`);
          if (r.data.status === "done") {
            setReport(r.data.report); setGenLoading(false);
            const id = profile.identity || {};
            const intake = { ...id, city_state: [id.city, id.state].filter(Boolean).join(", ") };
            try { await axios.post(`${API}/parish/save`, { intake, report: r.data.report }); } catch { /* ignore */ }
          }
          else if (r.data.status === "error") { alert(r.data.detail || "Generation failed."); setGenLoading(false); }
          else if (attempts > 60) { alert("Taking longer than expected. Please try again."); setGenLoading(false); }
          else setTimeout(poll, 3000);
        } catch { if (attempts > 60) setGenLoading(false); else setTimeout(poll, 3000); }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setGenLoading(false);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail && typeof detail === "object") setUpgrade(detail.message);
      else alert((typeof detail === "string" ? detail : detail?.message) || "Could not generate insights.");
    }
  };

  return (
    <div className="pp" data-testid="parish-profile-page">
      <div className="pp-top">
        <div className="pp-brand">
          <div className="b1">Parish <span>Profile</span></div>
          <div className="b2">Liturgical Intelligence™ · Living Parish Context</div>
        </div>
        <div className="pp-top-right">
          {savedAt && <span className="pp-saved-at">Saved {new Date(savedAt).toLocaleString()}</span>}
          <button className="pp-save" onClick={() => save(false)} disabled={saving} data-testid="profile-save-btn">
            {saving ? "Saving…" : "Save Profile"}
          </button>
          <button className="pp-back" onClick={() => navigate("/dashboard")} data-testid="pp-back">← Dashboard</button>
        </div>
      </div>

      <div className="pp-tabs" data-testid="pp-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`pp-tab${active === t.key ? " active" : ""}${completeness.frac[t.key] >= 0.8 ? " done" : ""}`}
            onClick={() => switchTab(t.key)} data-testid={`tab-${t.key}`}>
            {TAB_WEIGHTS[t.key] && <span className="pp-tab-dot" aria-hidden="true" />}{t.label}
          </button>
        ))}
      </div>

      <div className="pp-meter" data-testid="profile-completeness">
        <div className="pp-meter-main">
          <div className="pp-meter-top">
            <span className="pp-meter-label">Profile Completeness</span>
            <span className="pp-meter-pct" data-testid="completeness-pct">{completeness.percent}%</span>
          </div>
          <div className="pp-meter-bar"><i style={{ width: `${completeness.percent}%` }} /></div>
        </div>
        <div className="pp-meter-nudge" data-testid="completeness-nudge">
          {completeness.percent >= 100
            ? "Your profile is complete — Ordo AI™ has full context to work with."
            : completeness.sugg.length
              ? <>Your profile is <b>{completeness.percent}%</b> complete. Complete <b>{completeness.sugg.join(" and ")}</b> to sharpen Ordo AI™ recommendations.</>
              : <>Your profile is <b>{completeness.percent}%</b> complete. Keep adding detail to sharpen Ordo AI™ recommendations.</>}
        </div>
      </div>

      <div className="pp-memory-summary" data-testid="pp-memory-summary">
        <div className="pp-mem-info">
          <span className="pp-mem-label">Parish Memory™</span>
          <span className="pp-mem-desc">
            {memStats?.subscribed
              ? <>Ordo AI™ is building your parish's institutional knowledge. <b>{memStats.total || 0}</b> memories · <b>{memStats.locked || 0}</b> locked · <b>{memStats.avg_confidence || 0}%</b> avg confidence.</>
              : <>Ordo AI™ learns how your parish worships from every generation. Unlock permanent Parish Memory with Parish Pro.</>}
          </span>
        </div>
        <button className="pp-mem-btn" onClick={() => navigate("/parish-memory")} data-testid="pp-open-memory">Open Parish Memory™</button>
      </div>

      {memStats?.needs_review > 0 && (
        <div className="pp-review-banner" data-testid="pp-review-banner">
          <span>Your Liturgical Library™ has changed. Some older Parish Memory™ entries about worship resources may need review.</span>
          <button onClick={() => navigate("/parish-memory")} data-testid="pp-review-open">Review now</button>
        </div>
      )}

      <div className="pp-body">
        {tab.type === "object" && (
          <>
            {tab.key === "worship_identity" && (
              <div className="pp-ci-intro" data-testid="ci-intro">
                <p className="pp-ci-lead">Congregational Identity™ helps Ordo AI™ understand that every parish is more than a denomination. It reflects the liturgical, musical, cultural, pastoral, and communal layers that shape how your congregation worships.</p>
                <div className="pp-ci-helper" data-testid="ci-helper">
                  <h4>How Ordo AI™ Uses Congregational Identity™</h4>
                  <p>These selections influence:</p>
                  <ul>
                    <li>hymn and music recommendations</li>
                    <li>liturgical planning</li>
                    <li>seasonal customs</li>
                    <li>bulletin language</li>
                    <li>choir and worship team planning</li>
                    <li>Parish Memory™</li>
                    <li>Musical Intelligence™</li>
                    <li>future Formation and Diocesan Intelligence™</li>
                  </ul>
                </div>
              </div>
            )}
            {tab.groups ? (
              <div className="pp-groups">
                {tab.groups.map((g, gi) => (
                  <section className="pp-group-card" key={g.title} data-testid={`group-${tab.key}-${gi}`}>
                    <header className="pp-group-header">
                      <h2 className="pp-group-title">{g.title}</h2>
                      {g.subtitle && <p className="pp-group-subtitle">{g.subtitle}</p>}
                    </header>
                    <div className="pp-grid">
                      {g.fields.map((f) => (
                        <Field
                          key={f[0]}
                          def={f}
                          value={(profile[tab.key] || {})[f[0]]}
                          onChange={(v) => setObj(tab.key, f[0], v)}
                          siblings={profile[tab.key] || {}}
                          onSiblingChange={(sk, sv) => setObjSibling(tab.key, sk, sv)}
                          traditionKey={traditionKey}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="pp-grid">
                {tab.fields.map((f) => (
                  <Field
                    key={f[0]}
                    def={f}
                    value={(profile[tab.key] || {})[f[0]]}
                    onChange={(v) => setObj(tab.key, f[0], v)}
                    siblings={profile[tab.key] || {}}
                    onSiblingChange={(sk, sv) => setObjSibling(tab.key, sk, sv)}
                    traditionKey={traditionKey}
                  />
                ))}
              </div>
            )}
            {tab.key === "worship_identity" && (
              <LiturgicalLibrarySection
                catalog={catalog}
                wi={profile.worship_identity || {}}
                update={(k, v) => setObj("worship_identity", k, v)}
              />
            )}
          </>
        )}

        {tab.type === "worship_music" && (
          <WorshipMusicSection
            data={profile.worship_music || {}}
            legacyMusic={profile.music || {}}
            update={(k, v) => setObj("worship_music", k, v)}
          />
        )}

        {tab.type === "music_resources" && (
          <MusicResourcesSection
            data={profile.music_resources || {}}
            update={(k, v) => setObj("music_resources", k, v)}
          />
        )}

        {tab.type === "list" && (
          <div className="pp-list">
            {(profile[tab.key] || []).map((item, idx) => (
              <div className="pp-item" key={idx} data-testid={`${tab.key}-item-${idx}`}>
                <div className="pp-item-head">
                  <span>{tab.label} #{idx + 1}{item[tab.itemName] ? ` · ${item[tab.itemName]}` : ""}</span>
                  <button className="pp-remove" onClick={() => removeItem(tab.key, idx)} data-testid={`${tab.key}-remove-${idx}`}>Remove</button>
                </div>
                <div className="pp-grid">
                  {tab.fields.map((f) => (
                    <Field
                      key={f[0]}
                      def={f}
                      value={item[f[0]]}
                      onChange={(v) => setItem(tab.key, idx, f[0], v)}
                      siblings={item}
                      onSiblingChange={(sk, sv) => setItemSibling(tab.key, idx, sk, sv)}
                      traditionKey={traditionKey}
                    />
                  ))}
                </div>
              </div>
            ))}
            <button className="pp-add" onClick={() => addItem(tab.key)} data-testid={`add-${tab.key}`}>+ {tab.addLabel}</button>
          </div>
        )}

        {tab.type === "customs" && (
          <div className="pp-customs">
            {tab.seasons.map((season) => {
              const sk = seasonKey(season);
              const data = (profile.customs || {})[sk] || {};
              const open = openSeason === season;
              return (
                <div className={`pp-season${open ? " open" : ""}`} key={season} data-testid={`season-${sk}`}>
                  <button className="pp-season-h" onClick={() => setOpenSeason(open ? "" : season)} data-testid={`season-toggle-${sk}`}>
                    <span>{season}</span><span className="chev">{open ? "−" : "+"}</span>
                  </button>
                  {open && (
                    <div className="pp-grid">
                      {tab.fields.map((f) => (
                        <Field
                          key={f[0]}
                          def={f}
                          value={data[f[0]]}
                          onChange={(v) => setCustom(season, f[0], v)}
                          siblings={data}
                          onSiblingChange={(sk, sv) => setCustomSibling(season, sk, sv)}
                          traditionKey={traditionKey}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab.type === "insights" && (
          <div className="pp-insights">
            {upgrade && (
              <div className="pp-upsell" data-testid="profile-upgrade">
                <h3>Report limit reached</h3>
                <p>{upgrade}</p>
                <button className="pp-save" onClick={() => navigate("/account")} data-testid="profile-upsell-btn">Upgrade to Parish Pro</button>
              </div>
            )}
            {!report && !genLoading && !upgrade && (
              <div className="pp-insights-intro">
                <h2>Generate intelligence from your parish profile.</h2>
                <p>Ordo AI™ analyzes everything you've entered — identity, worship, clergy, music, customs and preferences — to produce an executive-grade Parish Intelligence Report. The richer your profile, the sharper the insight.</p>
                <button className="pp-generate" onClick={generate} data-testid="generate-insights-btn">Generate Parish Intelligence</button>
              </div>
            )}
            {genLoading && (
              <div className="pp-loading" data-testid="insights-loading">
                <div className="pp-spin" />
                <h3>Ordo AI™ is analyzing your parish…</h3>
                <p>Synthesizing your living parish context. This takes up to ~90 seconds.</p>
              </div>
            )}
            {report && (
              <div data-testid="insights-report">
                <ReportView report={report} meta={{ parish_name: (profile.identity || {}).parish_name, created_at: new Date().toISOString() }} />
                <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <button className="pp-generate report-no-print" onClick={generate} data-testid="regenerate-btn">Regenerate</button>
                  <button className="pp-add report-no-print" onClick={() => navigate("/reports")} data-testid="view-history-btn">View Report History</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
